import React, { useState } from "react";

/**
 * ModelReasoningCard
 * ──────────────────
 * "Thoughts" panel — minimal design matching reference UI.
 * Clean gray border, bullet list of plain-text thoughts, collapsible.
 *
 * Props
 * -----
 * steps          : Array of { intent, actions, data_context }
 * defaultExpanded: boolean (default false)
 */

// ── UUID filter — dataset refs are internal, never shown to users ──────────
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUUID = (s) => UUID_RE.test(String(s).trim());

// ── Map tool action labels → plain human sentences ─────────────────────────
const ACTION_SENTENCES = {
  "List connections":        "Checking which data sources are connected",
  "Check connection status": "Verifying the connection is active",
  "Load schema cache":       "Loading the schema from cache",
  "List data entities":      "Browsing available tables and objects",
  "Describe entity schema":  "Reading the table structure",
  "Preview sample rows":     "Sampling data to understand the structure",
  "Preview SQL query":       "Querying the data source",
  "Execute query":           "Running the query",
  "Cancel query":            "Cancelling the pending query",
  "Aggregate data":          "Calculating totals, counts, and averages",
  "Compare periods":         "Comparing values across two time periods",
  "Join datasets":           "Joining data from multiple sources",
  "Filter rows":             "Filtering the results to match the criteria",
  "Pivot dataset":           "Reshaping the data for a side-by-side view",
  "Format metric card":      "Formatting the final metric result",
  "Format data table":       "Building the results table",
  "Format query plan":       "Planning the multi-step analysis",
  "Recall user context":     "Recalling your preferences and saved context",
  "Save user fact":          "Saving a fact you shared for future reference",
};

const toSentence = (action) =>
  ACTION_SENTENCES[action] ||
  action.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ");

// ── Convert one agent step into a readable thought string ──────────────────
const buildThought = (step) => {
  // Use the LLM's own explanation if it's meaningful text
  if (step.intent && step.intent.trim().length > 10) {
    return step.intent
      .replace(/\*\*/g, "")
      .replace(/\*/g, "")
      .split("\n")[0]
      .trim();
  }

  if (!step.actions || step.actions.length === 0) return null;

  const sentences = step.actions.map(toSentence);

  // Append meaningful data references (not UUIDs, not very long strings)
  const refs = (step.data_context || []).filter(
    (r) => !isUUID(r) && r.length < 80
  );

  const base = sentences.join(", then ");
  return refs.length > 0 ? `${base} — ${refs.join(", ")}` : base;
};

// ── Main component ─────────────────────────────────────────────────────────

const ModelReasoningCard = ({ steps, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!steps || steps.length === 0) return null;

  const thoughts = steps
    .map(buildThought)
    .filter(Boolean);

  if (thoughts.length === 0) return null;

  return (
    <div className="w-full max-w-2xl rounded-xl border border-blue-100 bg-white shadow-sm overflow-hidden mt-1">

      {/* ── Header ── */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-1.5 bg-blue-50/40 hover:bg-blue-50/80 transition-colors"
      >
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5"
              d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span className="text-[0.85rem] font-bold text-blue-800">
            Thoughts
          </span>
        </div>

        {/* Chevron */}
        <svg
          className={`w-4 h-4 text-blue-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* ── Thoughts list ── */}
      {expanded && (
        <div className="px-4 pb-3 pt-2 space-y-0 relative border-t border-blue-50">
          {thoughts.map((thought, i) => (
            <div key={i} className="relative flex items-start gap-3 py-1.5">
              {/* Connector line */}
              {i < thoughts.length - 1 && (
                <div className="absolute left-[4px] top-[22px] bottom-[-6px] w-[2px] bg-blue-100 rounded-full" />
              )}
              {/* Vibrant blue dot */}
              <div className="relative shrink-0 mt-[6px] z-10">
                <div className="w-2.5 h-2.5 rounded-full bg-blue-500 ring-[3px] ring-blue-50" />
              </div>
              <p className="text-[0.95rem] text-black font-normal leading-snug">
                {thought}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ModelReasoningCard;
